/**
 * Shared print utilities for the application.
 * These utilities provide common functionality for print-related features.
 */

/**
 * Triggers the browser's print dialog.
 * This is the standard way to initiate printing across all browsers.
 */
export function triggerPrint(): void {
  window.print();
}

/**
 * Formats a date string or Date object for print-friendly display.
 * Returns dates in MM/dd/yyyy format which is suitable for printing.
 *
 * @param date - Date string (ISO format), Date object, or null/undefined
 * @returns Formatted date string or 'N/A' if input is invalid
 */
export function formatDateForPrint(
  date: string | Date | null | undefined
): string {
  if (!date) return 'N/A';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return 'N/A';

    return dateObj.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
  } catch {
    return 'N/A';
  }
}

/**
 * Formats a number as currency for print-friendly display.
 * Uses USD currency format with proper comma separators and decimal places.
 *
 * @param amount - Number to format as currency
 * @param includeCurrencySymbol - Whether to include the $ symbol (default: true)
 * @returns Formatted currency string
 */
export function formatCurrencyForPrint(
  amount: number,
  includeCurrencySymbol = true
): string {
  if (typeof amount !== 'number' || isNaN(amount)) return 'N/A';

  const formatted = amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return includeCurrencySymbol ? `$${formatted}` : formatted;
}

/**
 * CSS class names for managing print-specific styling.
 * These classes should be defined in global CSS with @media print rules.
 */
export const printClasses = {
  // Classes to hide elements in print view
  printHidden: 'print:hidden',
  screenOnly: 'screen-only',

  // Classes to show elements only in print view
  printOnly: 'print-only',
  printVisible: 'print:block',

  // Layout classes for print optimization
  printFullWidth: 'print:w-full',
  printNoBreak: 'print:break-inside-avoid',
  printBreakBefore: 'print:break-before-page',
  printBreakAfter: 'print:break-after-page',

  // Text styling for print
  printTextSm: 'print:text-sm',
  printTextXs: 'print:text-xs',
  printFontNormal: 'print:font-normal',

  // Color classes for print (black and white)
  printTextBlack: 'print:text-black',
  printBgWhite: 'print:bg-white',
  printBorderBlack: 'print:border-black',

  // Text wrapping and overflow classes for print tables
  printWordWrap: 'print:break-words',
  printWordBreak: 'print:break-all',
  printTextWrap: 'print:text-wrap',
  printWhitespaceNormal: 'print:whitespace-normal',
  printOverflowWrap: 'print:overflow-wrap-break-word',

  // Row height control for print tables
  printMinRowHeight: 'print:min-h-[0.75rem]',
  printMaxRowHeight: 'print:max-h-[2rem]',
  printRowHeightAuto: 'print:h-auto',

  // Cell padding and alignment for print
  printCellPaddingXs: 'print:p-1',
  printCellPaddingSm: 'print:p-2',
  printVerticalTop: 'print:align-top',
  printVerticalMiddle: 'print:align-middle',
} as const;

/**
 * Helper function to combine print-specific CSS classes with existing classes.
 * Useful for conditionally applying print styles to components.
 *
 * @param baseClasses - Existing CSS classes
 * @param printClasses - Print-specific classes to add
 * @returns Combined class string
 */
export function combineWithPrintClasses(
  baseClasses: string,
  printClasses: string
): string {
  return `${baseClasses} ${printClasses}`.trim();
}

/**
 * Utility to check if the current context is a print preview or print operation.
 * This can be useful for components that need to behave differently during printing.
 *
 * @returns true if currently in print context, false otherwise
 */
export function isPrintContext(): boolean {
  return window.matchMedia('print').matches;
}

/**
 * Text handling strategy for print content
 */
export type TextHandlingStrategy = 'wrap' | 'truncate' | 'auto';

/**
 * Configuration for handling text content in print layouts
 */
export interface PrintTextConfig {
  strategy: TextHandlingStrategy;
  maxLength?: number; // For truncation strategy
  maxLines?: number; // For wrapping strategy
  priority: 'preserve-content' | 'preserve-layout';
}

/**
 * Determines the best text handling strategy based on content type and print requirements.
 *
 * @param contentType - Type of content ('critical', 'important', 'standard')
 * @param maxAvailableWidth - Available width in characters (approximate)
 * @param textLength - Length of the text content
 * @returns Recommended text handling configuration
 */
export function getOptimalTextHandling(
  contentType: 'critical' | 'important' | 'standard',
  maxAvailableWidth: number = 30,
  textLength: number = 0
): PrintTextConfig {
  // For critical content (like denial reasons), prioritize preserving content
  if (contentType === 'critical') {
    return {
      strategy: textLength > maxAvailableWidth * 2 ? 'wrap' : 'auto',
      maxLines: 3,
      priority: 'preserve-content',
    };
  }

  // For important content (like dealer names), balance content and layout
  if (contentType === 'important') {
    return {
      strategy: textLength > maxAvailableWidth ? 'wrap' : 'auto',
      maxLength: maxAvailableWidth * 1.5,
      maxLines: 2,
      priority: 'preserve-content',
    };
  }

  // For standard content, prioritize layout consistency
  return {
    strategy: textLength > maxAvailableWidth ? 'truncate' : 'auto',
    maxLength: maxAvailableWidth,
    priority: 'preserve-layout',
  };
}
